import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import { parseCookies } from 'nookies/dist';
import PropTypes from 'prop-types';
import { RefObject, useEffect, useMemo, useRef, useState } from 'react';

import { getProductSuccess } from '@/redux/slices/product.slice';
import { addApiErrorToast } from '@/redux/slices/toast.slice';

import {
  NS
} from '@/constants';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { usePrimeurUser } from '@/hooks/usePrimeurUser';

import { computeBreadcrumb } from '@/utils/breadcrumbUtils';
import { getCategImage, getCategImageWebp } from '@/utils/categUtils';
import { isUserEmpty } from '@/utils/comparisonUtils';
import { formatUrl } from '@/utils/linkUtils';
import {
  carouselProductsToSlidesProps,
  isFeatureEnabled,
} from '@/utils/millesimaUtils';
import {
  findBottleByConditionnement,
  getAttribute,
  getAttributeId,
  getAttributeValue,
  getCurrentItem,
  transformAttributes
} from '@/utils/productUtils';
import { getAllProductsBrandLabel, getCriticsRatingNotes } from '@/utils/productCardUtils';
import { scrollToElement } from '@/utils/scroll';
import { useAppDispatch, useAppSelector } from '@/redux';
import { ItemModel } from '@/models/Product.model';
import { useServices } from '@/services/useServices';
import { Rating } from '@/types/rating';

const ProductImageZoomModal = dynamic(() => import('@/components/product/ProductImageZoomModal'), { ssr: false });
ProductImageZoomModal.displayName = 'ProductImageZoomModal';
const ReassuranceModal = dynamic(() => import('@/components/product/ReassuranceModal'), { ssr: false });
ReassuranceModal.displayName = 'ReassuranceModal';

import BreadcrumbContainer from '@/components/common/BreadcrumbContainer';
import JsonLd from '@/components/common/JsonLd';
import TopBannerEspot from '@/components/layout/espots/TopBannerEspot';
import Push from '@/components/common/design-system/Push.ds';
import ProductRecoCarousel from '@/components/layout/carouselV3/ProductRecoCarousel';
import FooterProductPageDynamicLinks from '@/components/layout/footer/FooterProductPageDynamicLinks';
import MetaTagsMillesima from '@/components/layout/MetaTagsMillesima';
import ModalAlertPrimeurInfos from '@/components/primeurAlert/ModalAlertPrimeurInfos';
import AddProductToCart from '@/components/product/AddProductToCart';
import ImageLink from '@/components/product/ImageLink';
import PrivateCellar from '@/components/product/PrivateCellar';
import ProductDetails from '@/components/product/ProductDetails';
import ProductImageAndActions from '@/components/product/ProductImageAndActions';
import ProductNotation from '@/components/product/ProductNotation';
import buildLinkedData from '@/linkedData/product-lkd';

import styles from './ProductView.module.scss';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { ANIMATION } from '@/constants/animation';
import { ATTRIBUTES } from '@/constants/product';
import { COOKIES } from '@/constants/cookies';
import { FEATURES } from '@/constants/features';
import { STORE_ID } from '@/constants/stores';
import { ProductNotationMobile } from '@/components/product/ProductNotation.mobile';

const NAMESPACES = [
  NS.COMMON,
  NS.PRODUCT,
  NS.CUSTOMIZATION,
  NS.CHECKOUT,
  NS.CATEG
];

export const ProductView = ({
  ratings,
  item,
  originalUrl,
  hashUserFromQuery,
  productForEc,
  ratingValue,
  ratingCount,
  relatedProducts,
  topBannerClosed,
}) => {
  const dispatch = useAppDispatch();
  const { storeConf } = useAppSelector(({ root }) => root);
  const { isDesktop, isMobile } = useScreenSizeContext();

  const isMobileDevice = useAppSelector(({ gui }) => gui.isMobileDevice);
  const productImageZoomVisible = useAppSelector(({ gui }) => gui.productImageZoomVisible);
  const reassurance = useAppSelector(({ gui }) => gui.reassurance);
  const reassuranceVisible = useAppSelector(({ gui }) => gui.reassuranceVisible);
  const alertPrimeurInfosModalVisible = useAppSelector(({ gui }) => gui.alertPrimeurInfosModalVisible);

  const { loggedUser } = useAppSelector(({ auth }) => auth);
  const { products } = useAppSelector(({ box }) => box.content);
  const isEc = isUserEmpty(loggedUser) ? false : loggedUser?.isEc;
  const { content: product, id: productId } = useAppSelector(({ product }) => product);
  const datalayerService = useServices().datalayerService;
  const productsService = useServices().productsService;
  const [currentItem, setCurrentItem] = useState<ItemModel>(getCurrentItem(product, item) as ItemModel);
  const [productAndItemAttributes, setProductAndItemAttributes] = useState<object[]>([]);

  const isAlert = getAttributeId(product, 'alerte_o_n') === '1';
  const { url: brandUrl, value: brand, isSpirit, imageCateg: imageCategProducer } = getAttribute(product, ATTRIBUTES.PRODUCER);
  const regionValue = getAttributeValue(product, ATTRIBUTES.REGION);

  const items = product.items || [];
  let itemJson = findBottleByConditionnement(items);
  const { t } = useTranslation(NAMESPACES);
  const wineboxRef = useRef<HTMLElement>();
  const attributesRef = useRef<HTMLElement>();
  const addToCartRef = useRef<HTMLElement>();
  const targetNodeRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>();

  const showAddToCart = useIntersectionObserver({ targetNodeRef });

  const [quantity, setQuantity] = useState(1);
  const [displayAddProductToCart, setDisplayAddProductToCart] = useState(false);
  const [topBannerAlreadyClosed, setTopBannerAlreadyClosed] = useState(topBannerClosed);

  usePrimeurUser({ hashUserFromQuery, isAlertable: getAttributeId(product, 'alerte_o_n') });

  const otherProductsFromProducer = product.otherProductOfProducteur ?? [];
  const slidesForOtherProductsFromProducerCarousel = otherProductsFromProducer.length > 0 &&
    carouselProductsToSlidesProps({ products: otherProductsFromProducer });
  const slidesForRelatedProductsCarousel = relatedProducts.length > 0 &&
    carouselProductsToSlidesProps({ products: relatedProducts });

  if (itemJson.length > 0) {
    itemJson = itemJson[0];
  } else if (product.items?.length > 0) {
    itemJson = product.items[0];
  }

  const json = buildLinkedData(originalUrl, product, itemJson, storeConf.currencyISO, ratingValue, ratingCount);

  const breadcrumbSeo = useMemo(() => computeBreadcrumb(product), [product]);

  useEffect(() => {
    setCurrentItem(getCurrentItem(product, item) as ItemModel);
  }, [product]);

  useEffect(() => {
    datalayerService.refreshProductDataLayer(product, currentItem);
  }, []);

  useEffect(() => {
    const shouldDisplay = () => {
      if (!product.display || !product) { // product isPrivate and no loggedUser
        return false;
      } else if (product.exclusive_cellar && !loggedUser?.isEc) {
        return false;
      } else if (product.nbItems === 0 && product.productName) {
        return false;
      } else if (currentItem.stock === 0) {
        return false;
      } else {
        return true;
      }
    };
    setDisplayAddProductToCart(() => shouldDisplay());
  }, [product, loggedUser, currentItem]);

  useEffect(() => {
    setProductAndItemAttributes(transformAttributes(product, currentItem));
  }, [currentItem]);

  const handleItemChange = (item) => {
    setCurrentItem(product.items.find((i) => i.partnumber === item));
  };

  useEffect(() => {
    // After connection with an Exclusive Cellar account, we must get EC product
    if (isEc && !productForEc) {
      if (loggedUser?.isEc) {
        if (typeof productId !== 'string') {
          return;
        }
        productsService.getProduct(productId).then((product: any) => {
          dispatch(getProductSuccess({ id: product._id, product }));
        }).catch((error) => {
          addApiErrorToast({ error });
        });
      }
    }
  }, [isEc]);

  useEffect(() => {
    // After connection on ghost page product (product without any info), we must reload to get product
    if (!isUserEmpty(loggedUser) && !product._id) {
      if (typeof productId !== 'string') {
        return;
      }
      productsService.getProduct(productId).then((product: any) => {
        dispatch(getProductSuccess({ id: product._id, product }));
      }).catch((error) => {
        addApiErrorToast({ error });
      });
    }
  }, [loggedUser]);

  const scrollToWineboxPanel = () => {
    scrollToElement(wineboxRef, { behavior: 'smooth', block: 'center', inline: 'start' });
  };

  const scrollToAttributes = () => {
    scrollToElement(attributesRef, { behavior: 'smooth', block: 'start', inline: 'start' });
  };

  const classNameAddToCart = [
    styles['product-add-to-cart'],
    showAddToCart ? `${styles.showAddToCart} ${ANIMATION.FROM_TOP}` : ANIMATION.TO_TOP
  ].join(' ');

  return (
    <>
      <MetaTagsMillesima
        product={product}
        originalUrl={originalUrl}
        isMobileDevice={isMobileDevice}
        item={item}
        isSpirit={product.isSpirit}
        noIndex={!product}
      />
      {alertPrimeurInfosModalVisible && (
        <ModalAlertPrimeurInfos isOpen={alertPrimeurInfosModalVisible} />
      )}
      <JsonLd data={json} />

      {product ? (
        <>
          <div className={`${styles.container} ${styles['new-style']}`} sensefuel-id="ProductView">
            {isDesktop && product.espotTopBanner && !topBannerAlreadyClosed && (
              <TopBannerEspot
                espot={product.espotTopBanner}
                topBannerAlreadyClosed={topBannerAlreadyClosed}
                setTopBannerAlreadyClosed={setTopBannerAlreadyClosed}
                contentClassname={styles.layout}
              />
            )}
            {(isDesktop || (!isDesktop && !products?.length)) && displayAddProductToCart && (
              <div className={classNameAddToCart} data-test-id="add-product-to-cart">
                <div className={`${styles.layout} ${styles['grid-product-add-to-cart']}`}>
                  <AddProductToCart
                    product={product}
                    currentItem={currentItem}
                    quantity={quantity}
                    setQuantity={setQuantity}
                    isAlert={isAlert}
                  />
                </div>
              </div>
            )}

            {isDesktop && (
              <div className={`${styles.navbar} ${styles.layout}`}>
                <BreadcrumbContainer
                  homeLabel={product.isSpirit ? t('breadcrumbHomeLabelSpirit') : t('breadcrumbHomeLabel')}
                  breadcrumbSeo={breadcrumbSeo}
                  baseUrl={storeConf.url}
                />
              </div>
            )}

            {productImageZoomVisible && (
              <ProductImageZoomModal
                bonus={product.bonus}
                video={product.video}
                image={currentItem.image ? currentItem.image : product.image}
                productName={product.productName}
              />
            )}

            <div className={`${styles.layout} ${styles['grid-image-actions']}`}>
              <ProductImageAndActions
                t={t}
                items={items}
                product={product}
                quantity={quantity}
                currentItem={currentItem}
                setQuantity={setQuantity}
                addToCartRef={addToCartRef}
                handleItemChange={handleItemChange}
                scrollToAttributes={(isDesktop || null) && scrollToAttributes}
                scrollToWineboxPanel={scrollToWineboxPanel}
              />
            </div>

            <div ref={targetNodeRef} className={styles['global-layout']}>
              <div className={`${styles.layout} ${styles['grid-content']}`}>
                <div className={styles.content}>
                  {isDesktop && (ratings.length > 0 || product.parkerTastingNote) &&
                      <ProductNotation
                        ratings={ratings}
                      />
                  }
                  <ProductDetails
                    t={t}
                    ratings={ratings}
                    product={product}
                    isDesktop={isDesktop}
                    ratingOrder={storeConf.ratingOrder}
                    productAndItemAttributes={productAndItemAttributes}
                    attributesRef={attributesRef}
                  />
                  {!isDesktop && product.espotPush && (
                    <Push
                      {...product.espotPush}
                    />
                  )}
                  {isMobile && (
                    <ImageLink
                      title={getAllProductsBrandLabel(t, regionValue, brand, isSpirit)}
                      image={getCategImage(imageCategProducer)}
                      imageWebp={getCategImageWebp(imageCategProducer)}
                      url={formatUrl(brandUrl, isSpirit, storeConf)}
                      lazy
                      alt={t('alter:product.producer', { brand })}
                    />
                  )}
                </div>
              </div>
              <div className={`${styles.layout} ${styles['grid-content']}`}>
                <div className={styles.content}>
                  {relatedProducts?.length > 0 && (
                    <ProductRecoCarousel
                      withGap slides={slidesForRelatedProductsCarousel}
                      title={t('common:carousel.youWillAlsoLike')}
                      position="productView"
                      withFreeScrolling={!isDesktop}
                    />
                  )}
                  {otherProductsFromProducer?.length > 0 && (
                    <ProductRecoCarousel
                      withGap slides={slidesForOtherProductsFromProducerCarousel}
                      title={t('common:carousel.fromSameProducer')}
                      position="productView" origin="fromSameProducer"
                      withFreeScrolling={!isDesktop}
                    />
                  )}
                  <FooterProductPageDynamicLinks
                    title={t('footer.producersFromSameAppellation')}
                    dynamicLinks={product.producersFromSameAppellation}
                    storeConf={storeConf}
                  />
                </div>
              </div>
            </div>
            {!isDesktop && (
              <div className={`${styles.navbar}`}>
                <BreadcrumbContainer
                  homeLabel={product.isSpirit ? t('breadcrumbHomeLabelSpirit') : t('breadcrumbHomeLabel')}
                  breadcrumbSeo={breadcrumbSeo}
                  baseUrl={storeConf.url}
                />
              </div>
            )}
          </div>

          {reassuranceVisible && reassurance && (
            <ReassuranceModal reassurance={reassurance} isOpen={reassuranceVisible}/>
          )}
        </>
      ) : (
        <PrivateCellar t={t} />
      )}
    </>
  );
};
ProductView.propTypes = {
  item: PropTypes.string,
  originalUrl: PropTypes.string,
  hashUserFromQuery: PropTypes.string,
  otherProductsFromProducer: PropTypes.array,
  productForEc: PropTypes.bool,
  ratingValue: PropTypes.number,
  ratingCount: PropTypes.number,
  producersFromSameAppellation: PropTypes.array,
  productSSR: PropTypes.object,
  topBannerClosed: PropTypes.bool,
};

export default ProductView;
/**
 * Prefetch function  called on server side
 * @param {object} ctx The server side context
 * @param {object} store The server side application store
 * @param {object} props The seo page props
 * @return {object} Return an object containing the component props
 *    layoutOption : object containing the needed page layout (see _app component)
 *    namespaces : an array containing the needed translation namespaces
 */
export const prefetchView = async (ctx, { getState, dispatch, services }, props) => {
  const { query, req } = ctx;
  const {
    root: { baseUrl, storeConf },
    auth: { loggedUser },
    cart: { content: { storeIdForPrice: storeIdForPriceInCart } },
    gui: { storeIdForPrice: storeIdForPriceInStore }
  } = getState();
  const item = props.item || query.current || query.item;
  const productForEc = !!loggedUser?.isEc;
  const { productsService } = services;
  const { hashUser: hashUserFromQuery } = query;
  let conditionnement = '';
  let productId = props.id;

  if (req?.headers?.referer) {
    const refererSlashes = req.headers.referer.split('/');
    const refererParam = refererSlashes[refererSlashes.length - 1].split('?');
    if (refererParam.length > 1) {
      const refererParams = refererParam[1].split('&');
      refererParams.forEach((param) => {
        const [key, value] = param.split('=');
        if (key === 'filter' && /conditionnement/.test(value)) {
          conditionnement = value.split(':')[1];
        }
      });
    }
  }

  const storeIdForPrice = storeIdForPriceInStore || storeIdForPriceInCart;
  if (storeIdForPrice && storeConf.storeId === STORE_ID.COM && !productId.includes(storeIdForPrice)) {
    productId += `-${storeIdForPrice}`;
  }

  const product = props.product || (await productsService.getProduct(productId));

  if (product?.items) {
    product.items = product.items.filter((item) => item.offerPrice > 0);
  }

  dispatch(getProductSuccess({ id: productId, product }));

  const namespacesRequired = [NS.COMMON, NS.PRODUCT, NS.CHECKOUT, NS.CATEG, NS.ALTER];

  if (isFeatureEnabled(storeConf, FEATURES.MODULE_CUSTO)) {
    namespacesRequired.push(NS.CUSTOMIZATION);
  }

  if (loggedUser?.isAdmin) {
    namespacesRequired.push(NS.ADMIN);
  }

  const topBannerClosed = !!parseCookies(ctx)[`${COOKIES.TOP_BANNER_ESPOT.NAME}${product.espotTopBanner?._id}`];

  const ratings = await productsService.getCritics(product.partnumber, storeConf.locale.split('-')[0]);

  return {
    ratings,
    namespacesRequired,
    originalUrl: baseUrl,
    item,
    conditionnement,
    relatedProducts: [],
    hashUserFromQuery,
    // To know if we got Exclusive Cellar products
    productForEc,
    topBannerClosed
  };
};
